/* ============================================================
   DepartmentPicker — searchable multi-select of departments, each shown with
   its head (names can be near-identical). Moved out of the signup form; used by
   the post-verification department step (DepartmentSetup).
   ============================================================ */

import { useMemo, useState } from 'react'

/* "Creative Services - Bedding EU/UK" → ["Creative Services", "Bedding EU/UK"]: everything
   after the first spaced dash goes on its own line (Areeb's signup layout). */
function splitDeptName(name) {
  const m = /^(.+?)\s+[-–—]\s+(.+)$/.exec(name || '')
  return m ? [m[1], m[2]] : [name || '', '']
}

/**
 * @param departments  [{ id, name, department_head_name? }]
 * @param value        selected department ids
 * @param onChange     (ids) => void
 */
export default function DepartmentPicker({ departments, value, onChange, disabled = false, error = '', label }) {
  const [query, setQuery] = useState('')
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q
      ? departments.filter((d) => `${d.name} ${d.department_head_name || ''}`.toLowerCase().includes(q))
      : departments
  }, [query, departments])
  const toggle = (id) => onChange(value.includes(id) ? value.filter((d) => d !== id) : [...value, id])
  // Select all / clear acts only on what the search currently shows.
  const allVisible = matches.length > 0 && matches.every((d) => value.includes(d.id))
  const toggleAllVisible = () => {
    const visible = matches.map((d) => d.id)
    onChange(allVisible ? value.filter((id) => !visible.includes(id)) : [...value, ...visible.filter((id) => !value.includes(id))])
  }

  return (
    <div className="form-field su-depts">
      <div className="su-depts-head">
        <label htmlFor="dept-picker-search">
          {label}<span className="req"> *</span>
        </label>
        <span className="su-count" aria-live="polite">
          {value.length ? `${value.length} of ${departments.length} selected` : `${departments.length} available`}
        </span>
      </div>

      <div className={`su-picker${error ? ' is-err' : ''}`}>
        <div className="su-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input
            id="dept-picker-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search departments or HOD"
            autoComplete="off"
            disabled={disabled}
          />
          {query && (
            <button type="button" className="su-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </button>
          )}
        </div>

        {matches.length > 1 && (
          <div className="su-bulk">
            <span>{query ? `${matches.length} matches` : 'All departments'}</span>
            <button type="button" className="link-btn" onClick={toggleAllVisible} disabled={disabled}>
              {allVisible ? 'Clear all' : 'Select all'}
            </button>
          </div>
        )}

        <div role="group" aria-label="Departments" className="su-list">
          {matches.length === 0 ? (
            <div className="su-empty">
              No departments match “{query.trim()}”.
              <button type="button" className="link-btn" onClick={() => setQuery('')}>Clear search</button>
            </div>
          ) : (
            matches.map((d) => {
              const on = value.includes(d.id)
              const [group, area] = splitDeptName(d.name)
              return (
                <label key={d.id} className={`su-item${on ? ' is-on' : ''}`}>
                  <input type="checkbox" className="su-sr" checked={on} onChange={() => toggle(d.id)} disabled={disabled} />
                  <span className="su-avatar" aria-hidden="true">{d.name.slice(0, 2)}</span>
                  <span className="su-item-text">
                    <span className="su-item-name">{group}</span>
                    {area && <span className="su-item-area">{area}</span>}
                    <span className="su-item-sub">{d.department_head_name ? `HOD · ${d.department_head_name}` : 'No HOD'}</span>
                  </span>
                  <span className="su-box" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
                  </span>
                </label>
              )
            })
          )}
        </div>
      </div>

      <span className={`field-error${error ? ' show' : ''}`}>{error || ''}</span>
    </div>
  )
}
